import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Save, ArrowLeft, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import StepEditor from "@/components/StepEditor";
import CheckboxGrid from "@/components/CheckboxGrid";
import GuidedTour from "@/components/GuidedTour";
import { canManageSop } from "@/lib/permissions";
import { consumePendingTour } from "@/lib/tour";
import { TOUR_STEPS } from "@/lib/tourSteps";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";

const EMPTY_SOP = {
  site: "",
  service: "",
  titre: "",
  createur: "",
  validateur: "",
  date: new Date().toISOString().slice(0, 10),
  prerequis: "",
  steps: [],
  localisation_coactivite: [],
  energies_potentielles: [],
  epi_obligatoires: [],
  epi_specifiques: [],
  materiel_engins: [],
  habilitations: [],
  permis: [],
  mesures_generiques: [],
  retours_remarques: "",
  statut: "Actif",
};

const TABS = ["informations", "etapes", "localisation", "epi", "recap"];

export default function SopWizard() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const [reference, setReference] = useState({});
  const [form, setForm] = useState(EMPTY_SOP);
  const [tab, setTab] = useState("informations");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (!isEdit && !perms.can_create) {
      toast.error("Vous n'avez pas les droits pour créer une SOP");
      navigate("/dashboard");
    }
  }, [isEdit, perms.can_create, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const refRes = await api.get("/reference/all");
        setReference(refRes.data);
        if (isEdit) {
          const sopRes = await api.get(`/sops/${id}`);
          if (!canManageSop(user, sopRes.data)) {
            toast.error("Vous n'avez pas les droits pour modifier cette SOP");
            navigate(`/sops/${id}`);
            return;
          }
          setForm(sopRes.data);
        }
      } catch (e) {
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
        if (consumePendingTour("validator")) setRunTour(true);
      }
    };
    load();
  }, [id, isEdit, user, navigate]);

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const addStep = () => {
    const newStep = {
      numero: form.steps.length + 1,
      description: "",
      photos: [],
      moyens: [],
      risques_complementaires: [],
    };
    updateField("steps", [...form.steps, newStep]);
  };

  const updateStep = (idx, updatedStep) => {
    const steps = [...form.steps];
    steps[idx] = updatedStep;
    updateField("steps", steps);
  };

  const removeStep = (idx) => {
    const steps = form.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, numero: i + 1 }));
    updateField("steps", steps);
  };

  const validate = () => {
    if (!form.site) return "Le site est obligatoire";
    if (!form.service) return "Le service est obligatoire";
    if (!form.titre) return "Le titre de la SOP est obligatoire";
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      setTab("informations");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/sops/${id}`, form);
        toast.success("SOP mise à jour");
      } else {
        const res = await api.post("/sops", form);
        toast.success("SOP créée avec succès");
        navigate(`/sops/${res.data.id}`);
        return;
      }
      navigate(`/sops/${id}`);
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    const idx = TABS.indexOf(tab);
    if (idx < TABS.length - 1) setTab(TABS[idx + 1]);
  };
  const goPrev = () => {
    const idx = TABS.indexOf(tab);
    if (idx > 0) setTab(TABS[idx - 1]);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="sop-wizard-page">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-semibold text-foreground">
            {isEdit ? "Modifier la SOP" : "Nouvelle SOP"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Formulaire guidé étape par étape pour la création du mode opératoire
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 w-full mb-6" data-testid="wizard-tabs">
            <TabsTrigger value="informations" data-testid="wizard-tab-informations">
              Informations
            </TabsTrigger>
            <TabsTrigger value="etapes" data-testid="wizard-tab-etapes">
              Étapes
            </TabsTrigger>
            <TabsTrigger value="localisation" data-testid="wizard-tab-localisation">
              Localisation
            </TabsTrigger>
            <TabsTrigger value="epi" data-testid="wizard-tab-epi">
              EPI &amp; Permis
            </TabsTrigger>
            <TabsTrigger value="recap" data-testid="wizard-tab-recap">
              Récapitulatif
            </TabsTrigger>
          </TabsList>

          <TabsContent value="informations" className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Site *</Label>
                <Select value={form.site} onValueChange={(v) => updateField("site", v)}>
                  <SelectTrigger data-testid="form-site-select">
                    <SelectValue placeholder="Sélectionner le site" />
                  </SelectTrigger>
                  <SelectContent>
                    {form.site && !reference.sites?.includes(form.site) && (
                      <SelectItem value={form.site}>{form.site} (valeur importée)</SelectItem>
                    )}
                    {(perms.role === "national_admin" ? reference.sites : perms.sites)?.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Service *</Label>
                <Select value={form.service} onValueChange={(v) => updateField("service", v)}>
                  <SelectTrigger data-testid="form-service-select">
                    <SelectValue placeholder="Sélectionner le service" />
                  </SelectTrigger>
                  <SelectContent>
                    {form.service && !reference.services?.includes(form.service) && (
                      <SelectItem value={form.service}>{form.service} (valeur importée)</SelectItem>
                    )}
                    {reference.services?.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Titre / Description de la SOP *</Label>
              <Input
                value={form.titre}
                onChange={(e) => updateField("titre", e.target.value)}
                placeholder="Ex: Curage du bassin principal de l'usine"
                data-testid="form-titre-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Créateur</Label>
                <Input
                  value={form.createur}
                  onChange={(e) => updateField("createur", e.target.value)}
                  data-testid="form-createur-input"
                />
              </div>
              <div>
                <Label>Validateur</Label>
                <Input
                  value={form.validateur}
                  onChange={(e) => updateField("validateur", e.target.value)}
                  data-testid="form-validateur-input"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  data-testid="form-date-input"
                />
              </div>
            </div>

            <div>
              <Label>Pré-requis</Label>
              <Textarea
                value={form.prerequis}
                onChange={(e) => updateField("prerequis", e.target.value)}
                placeholder="Ex: Personne formée & autorisée - Plan de circulation maîtrisé..."
                data-testid="form-prerequis-input"
              />
            </div>
          </TabsContent>

          <TabsContent value="etapes" className="space-y-4">
            {form.steps.map((step, idx) => (
              <StepEditor
                key={idx}
                step={step}
                index={idx}
                moyensList={reference.moyens || []}
                onChange={(s) => updateStep(idx, s)}
                onRemove={() => removeStep(idx)}
              />
            ))}
            <Button variant="outline" className="w-full" onClick={addStep} data-testid="add-step-button">
              <Plus className="h-4 w-4" />
              Ajouter une étape
            </Button>
          </TabsContent>

          <TabsContent value="localisation" className="space-y-6">
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">
                Localisation du chantier / Coactivité
              </h3>
              <CheckboxGrid
                items={reference.localisation_coactivite || []}
                selected={form.localisation_coactivite}
                onChange={(v) => updateField("localisation_coactivite", v)}
                testIdPrefix="localisation"
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">
                Énergies potentielles à consigner
              </h3>
              <CheckboxGrid
                items={reference.energies_potentielles || []}
                selected={form.energies_potentielles}
                onChange={(v) => updateField("energies_potentielles", v)}
                testIdPrefix="energies"
              />
            </div>
          </TabsContent>

          <TabsContent value="epi" className="space-y-6">
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">EPI obligatoires</h3>
              <CheckboxGrid
                items={reference.epi_obligatoires || []}
                selected={form.epi_obligatoires}
                onChange={(v) => updateField("epi_obligatoires", v)}
                testIdPrefix="epi-obligatoires"
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">EPI spécifiques</h3>
              <CheckboxGrid
                items={reference.epi_specifiques || []}
                selected={form.epi_specifiques}
                onChange={(v) => updateField("epi_specifiques", v)}
                testIdPrefix="epi-specifiques"
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Matériel / Engins</h3>
              <CheckboxGrid
                items={reference.materiel_engins || []}
                selected={form.materiel_engins}
                onChange={(v) => updateField("materiel_engins", v)}
                testIdPrefix="materiel"
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">
                Habilitations / Formations nécessaires
              </h3>
              <CheckboxGrid
                items={reference.habilitations_formations || []}
                selected={form.habilitations}
                onChange={(v) => updateField("habilitations", v)}
                testIdPrefix="habilitations"
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Permis nécessaires</h3>
              <CheckboxGrid
                items={reference.permis_necessaires || []}
                selected={form.permis}
                onChange={(v) => updateField("permis", v)}
                testIdPrefix="permis"
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Mesures génériques</h3>
              <CheckboxGrid
                items={reference.mesures_generiques || []}
                selected={form.mesures_generiques}
                onChange={(v) => updateField("mesures_generiques", v)}
                testIdPrefix="mesures-generiques"
              />
            </div>
            <div>
              <Label>Retours & Remarques chantier vers la préparation</Label>
              <Textarea
                value={form.retours_remarques}
                onChange={(e) => updateField("retours_remarques", e.target.value)}
                data-testid="form-retours-remarques-input"
              />
            </div>
          </TabsContent>

          <TabsContent value="recap" className="space-y-4">
            <div className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="recap-summary">
              <p>
                <span className="font-semibold">Site :</span> {form.site || "—"}
              </p>
              <p>
                <span className="font-semibold">Service :</span> {form.service || "—"}
              </p>
              <p>
                <span className="font-semibold">Titre :</span> {form.titre || "—"}
              </p>
              <p>
                <span className="font-semibold">Nombre d'étapes :</span> {form.steps.length}
              </p>
              <p>
                <span className="font-semibold">Localisation/Coactivité sélectionnées :</span>{" "}
                {form.localisation_coactivite.length}
              </p>
              <p>
                <span className="font-semibold">Énergies potentielles sélectionnées :</span>{" "}
                {form.energies_potentielles.length}
              </p>
            </div>
            <Button
              className="w-full h-11"
              onClick={handleSubmit}
              disabled={saving}
              data-testid="submit-sop-button"
            >
              <Save className="h-4 w-4" />
              {saving ? "Enregistrement..." : isEdit ? "Mettre à jour la SOP" : "Enregistrer la SOP"}
            </Button>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between mt-6 no-print">
          <Button variant="outline" onClick={goPrev} disabled={tab === TABS[0]} data-testid="wizard-prev-button">
            <ArrowLeft className="h-4 w-4" />
            Précédent
          </Button>
          {tab !== "recap" ? (
            <Button onClick={goNext} data-testid="wizard-next-button">
              Suivant
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={saving} data-testid="wizard-final-submit-button">
              <Save className="h-4 w-4" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          )}
        </div>
      </div>
      <GuidedTour steps={TOUR_STEPS.validator} run={runTour} onFinish={() => setRunTour(false)} />
    </Layout>
  );
}
